import { useEffect, useRef } from 'react';
import {
  PLACE_ADDRESS_FIELDS,
  autocompleteOptionsKey,
  ensurePlaceAddressDetails,
} from '../utils/ensurePlaceAddressDetails';
import { placeHasAddressComponents } from '../utils/parseGooglePlaceAddress';

const useGooglePlacesAutocomplete = (onPlaceSelected, options = {}) => {
  const inputRef = useRef(null);
  const onPlaceSelectedRef = useRef(onPlaceSelected);
  const optionsRef = useRef(options);
  const requestIdRef = useRef(0);

  onPlaceSelectedRef.current = onPlaceSelected;
  optionsRef.current = options;

  const { enabled = true } = options;
  const optionsKey = autocompleteOptionsKey(options);

  useEffect(() => {
    if (!enabled) return undefined;

    let autocomplete;
    let cancelled = false;
    const scriptId = 'google-maps-script';

    const emitPlace = (place) => {
      const requestId = ++requestIdRef.current;
      Promise.resolve(ensurePlaceAddressDetails(place)).then((detailed) => {
        if (cancelled || requestId !== requestIdRef.current) return;
        if (!placeHasAddressComponents(detailed)) return;
        onPlaceSelectedRef.current?.(detailed);
      });
    };

    const initAutocomplete = () => {
      if (cancelled || !inputRef.current || !window.google?.maps?.places) return;

      const { enabled: _enabled, fields, ...autocompleteOptions } = optionsRef.current || {};
      autocomplete = new window.google.maps.places.Autocomplete(inputRef.current, {
        types: ['address'],
        ...autocompleteOptions,
        fields: [...new Set([...(fields || []), ...PLACE_ADDRESS_FIELDS])],
      });

      autocomplete.addListener('place_changed', () => {
        emitPlace(autocomplete.getPlace());
      });
    };

    if (!window.google?.maps?.places) {
      let script = document.getElementById(scriptId);
      if (!script) {
        script = document.createElement('script');
        script.id = scriptId;
        script.src = `https://maps.googleapis.com/maps/api/js?key=${import.meta.env.VITE_GOOGLE_MAPS_API_KEY}&libraries=places&v=beta`;
        script.async = true;
        document.head.appendChild(script);
      }
      script.addEventListener('load', initAutocomplete);
      return () => {
        cancelled = true;
        script.removeEventListener('load', initAutocomplete);
        if (autocomplete) {
          window.google?.maps?.event?.clearInstanceListeners(autocomplete);
        }
      };
    }

    initAutocomplete();
    return () => {
      cancelled = true;
      if (autocomplete) {
        window.google.maps.event.clearInstanceListeners(autocomplete);
      }
    };
  }, [enabled, optionsKey]);

  return inputRef;
};

export default useGooglePlacesAutocomplete;
